'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import { Users, Calendar, Star, Lock, ArrowRight, AlertCircle, Loader2, Heart, Zap, Shield } from 'lucide-react';

type PageState = 'loading' | 'populated' | 'unavailable';

const communityPillars = [
  {
    id: 'network',
    icon: Users,
    title: 'A Private Resident-First Network',
    body: 'The Panorama community platform is built for residents, by residents. It\u2019s not affiliated with building management or the HOA \u2014 it\u2019s a voluntary, private network that exists because residents wanted it.',
    accent: '#4CC9FF',
  },
  {
    id: 'knowledge',
    icon: Star,
    title: 'Trusted Recommendations & Local Knowledge',
    body: 'The best service providers, the best restaurants, the best shortcuts \u2014 residents share what they know. The directory, services section, and conversations are all built around resident-sourced intelligence.',
    accent: '#7A5CFF',
  },
  {
    id: 'events',
    icon: Calendar,
    title: 'Resident Events & Shared Interests',
    body: 'Monthly mixers, art shows, rooftop gatherings, and spontaneous outings. The community has a social life \u2014 and the platform helps residents stay connected to it.',
    accent: '#FF3DF5',
  },
  {
    id: 'inclusive',
    icon: Heart,
    title: 'A Community for Owners, Renters & Residents',
    body: 'Whether you own your unit, rent it, or use Panorama as a second home, you\u2019re a full member of the community. The platform is open to all verified residents.',
    accent: '#4CC9FF',
  },
  {
    id: 'voluntary',
    icon: Shield,
    title: 'Voluntary Participation, Real Connection',
    body: 'No one is required to participate. But residents who do tend to get more out of living here \u2014 better recommendations, more connections, and a stronger sense of belonging.',
    accent: '#7A5CFF',
  },
  {
    id: 'more',
    icon: Zap,
    title: 'Living at Panorama Means More Than a Great Unit',
    body: 'The building is exceptional. The community makes it something more. Residents who engage with the platform consistently say it changes how they feel about living here.',
    accent: '#FF3DF5',
  },
];

const vegasOutings = [
  { label: 'Golden Knights Games', icon: '🏒', description: 'T-Mobile Arena is minutes away. Residents often coordinate group attendance.' },
  { label: 'Raiders Games', icon: '🏈', description: 'Allegiant Stadium is a short drive. Game days are a community event.' },
  { label: 'The Sphere', icon: '🌐', description: 'The most immersive entertainment experience in Las Vegas. Residents plan ahead.' },
  { label: 'Conventions & Expos', icon: '🎪', description: 'CES, NAB, SEMA — Panorama is the ideal base for convention season.' },
  { label: 'Dining Outings', icon: '🍽️', description: 'From Michelin-starred to hidden gems — residents share the best tables.' },
  { label: 'Nightlife & Concerts', icon: '🎶', description: 'World-class venues, residencies, and shows. Residents go together.' },
];

export default function CommunityPage() {
  const [pageState, setPageState] = useState<PageState>('loading');

  useEffect(() => {
    const t = setTimeout(() => setPageState('populated'), 800);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="min-h-screen" style={{ background: '#07060C' }}>
      <SiteHeader theme="transparent" />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-20 overflow-hidden panorama-hero-bg">
          <div
            className="absolute top-0 right-0 w-[55%] h-[75%] pointer-events-none"
            style={{ background: 'radial-gradient(ellipse 70% 65% at 100% 5%, rgba(255,61,245,0.18) 0%, rgba(122,92,255,0.08) 50%, transparent 70%)' }}
          />
          <div className="relative z-10 max-w-screen-2xl mx-auto px-6 lg:px-10">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 mb-6"
              style={{ borderRadius: '6px', background: 'rgba(255,61,245,0.10)', border: '1px solid rgba(255,61,245,0.22)' }}
            >
              <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#FF3DF5' }} />
              <span className="text-[12px] font-medium tracking-wide" style={{ color: '#FF3DF5' }}>
                The Community
              </span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h1
                  className="font-display font-extrabold text-[48px] sm:text-[60px] lg:text-[68px] leading-[1.04] tracking-tight mb-5 max-w-3xl"
                  style={{ color: '#F2F4FF' }}
                >
                  Panorama is more{' '}
                  <span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(135deg, #FF3DF5 0%, #7A5CFF 50%, #4CC9FF 100%)' }}>
                    than a building.
                  </span>
                </h1>
                <p className="text-[18px] leading-relaxed max-w-2xl mb-10 font-light" style={{ color: 'rgba(242,244,255,0.72)' }}>
                  It's a real community — one where residents know each other, plan outings together, share recommendations, and look out for one another. The private platform exists because residents built it.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link href="/request-access" className="btn-primary group">
                    Request Access
                    <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-0.5" />
                  </Link>
                  <Link href="/lifestyle" className="btn-secondary">
                    See the Lifestyle
                  </Link>
                </div>
              </div>
              {/* Hero image placeholder */}
              <div
                className="hidden lg:flex h-[420px] rounded-xl flex-col items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,61,245,0.07) 0%, rgba(122,92,255,0.09) 50%, rgba(76,201,255,0.06) 100%)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <div
                  className="w-16 h-16 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
                >
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="rgba(167,163,196,0.30)" strokeWidth="1.5">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <path d="m21 15-5-5L5 21" />
                  </svg>
                </div>
                <p className="text-[13px] font-medium" style={{ color: 'rgba(167,163,196,0.40)' }}>Community photography coming soon</p>
                <p className="text-[11px] mt-1" style={{ color: 'rgba(167,163,196,0.25)' }}>Resident events, outings, and social life</p>
              </div>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 neon-divider" />
        </section>

        {/* Loading */}
        {pageState === 'loading' && (
          <section className="py-24" style={{ background: '#0C0A16' }}>
            <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 flex flex-col items-center justify-center py-20 gap-4">
              <Loader2 size={32} className="animate-spin" style={{ color: '#FF3DF5' }} />
              <p className="text-[15px]" style={{ color: '#A7A3C4' }}>Loading…</p>
            </div>
          </section>
        )}

        {/* Unavailable */}
        {pageState === 'unavailable' && (
          <section className="py-24" style={{ background: '#0C0A16' }}>
            <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 flex flex-col items-center justify-center py-20 gap-4 text-center">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,61,245,0.10)', border: '1px solid rgba(255,61,245,0.20)' }}>
                <AlertCircle size={22} style={{ color: '#FF3DF5' }} />
              </div>
              <h3 className="font-display font-semibold text-[20px]" style={{ color: '#F2F4FF' }}>Content unavailable</h3>
              <p className="text-[14px] max-w-sm" style={{ color: '#A7A3C4' }}>We're having trouble loading this page. Please try again shortly.</p>
              <Link href="/" className="btn-secondary text-[13px] px-4 py-2 mt-2">Return Home</Link>
            </div>
          </section>
        )}

        {/* Populated */}
        {pageState === 'populated' && (
          <>
            {/* Community pillars */}
            <section className="py-20" style={{ background: '#0C0A16' }}>
              <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
                <div className="text-center mb-14">
                  <p className="text-[12px] font-semibold tracking-[0.12em] uppercase mb-3" style={{ color: '#4CC9FF' }}>
                    What makes it real
                  </p>
                  <h2 className="font-display font-bold text-[36px] sm:text-[42px] leading-tight tracking-tight mb-4" style={{ color: '#F2F4FF' }}>
                    A community built by residents, for residents.
                  </h2>
                  <p className="text-[16px] max-w-xl mx-auto" style={{ color: '#A7A3C4' }}>
                    The Panorama community platform is voluntary, private, and resident-operated. Here's what that means in practice.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {communityPillars.map((p) => (
                    <div key={p.id} className="neon-card p-7">
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center mb-5"
                        style={{ background: `${p.accent}12`, border: `1px solid ${p.accent}22` }}
                      >
                        <p.icon size={18} style={{ color: p.accent }} />
                      </div>
                      <h3 className="font-display font-semibold text-[17px] mb-3" style={{ color: '#F2F4FF' }}>
                        {p.title}
                      </h3>
                      <p className="text-[13.5px] leading-relaxed" style={{ color: '#A7A3C4' }}>
                        {p.body}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* There's always something going on */}
            <section className="py-20" style={{ background: '#07060C' }}>
              <div className="neon-divider mb-0" />
              <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 pt-20">
                <div className="text-center mb-12">
                  <p className="text-[12px] font-semibold tracking-[0.12em] uppercase mb-3" style={{ color: '#7A5CFF' }}>
                    Life Beyond the Unit
                  </p>
                  <h2 className="font-display font-bold text-[36px] sm:text-[42px] leading-tight tracking-tight mb-4" style={{ color: '#F2F4FF' }}>
                    There's always something going on.
                  </h2>
                  <p className="text-[16px] max-w-xl mx-auto" style={{ color: '#A7A3C4' }}>
                    Las Vegas is one of the world's great entertainment cities. Panorama residents take full advantage of it — together.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {vegasOutings.map((outing) => (
                    <div key={outing.label} className="neon-card p-6 flex items-start gap-4">
                      <span className="text-[28px] flex-shrink-0">{outing.icon}</span>
                      <div>
                        <h3 className="font-display font-semibold text-[15px] mb-2" style={{ color: '#F2F4FF' }}>
                          {outing.label}
                        </h3>
                        <p className="text-[13px] leading-relaxed" style={{ color: '#A7A3C4' }}>
                          {outing.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Private platform teaser */}
            <section className="py-20" style={{ background: '#0C0A16' }}>
              <div className="neon-divider mb-0" />
              <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 pt-20">
                <div
                  className="relative overflow-hidden panorama-premium-bg rounded-xl p-10 lg:p-14"
                  style={{ border: '1px solid rgba(255,255,255,0.08)' }}
                >
                  <div className="absolute top-0 right-0 w-[50%] h-full pointer-events-none" style={{ background: 'radial-gradient(ellipse 80% 70% at 100% 20%, rgba(122,92,255,0.14) 0%, transparent 65%)' }} />
                  <div className="relative z-10">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                      <div>
                        <div
                          className="inline-flex items-center gap-2 px-3 py-1.5 mb-6"
                          style={{ borderRadius: '6px', background: 'rgba(122,92,255,0.10)', border: '1px solid rgba(122,92,255,0.22)' }}
                        >
                          <Lock size={12} style={{ color: '#7A5CFF' }} />
                          <span className="text-[12px] font-medium tracking-wide" style={{ color: '#7A5CFF' }}>
                            Member Feature Preview
                          </span>
                        </div>
                        <h2 className="font-display font-bold text-[32px] sm:text-[38px] leading-tight tracking-tight mb-5" style={{ color: '#F2F4FF' }}>
                          Coordinate plans. Invite neighbors. Make it happen.
                        </h2>
                        <p className="text-[16px] leading-relaxed mb-4" style={{ color: 'rgba(167,163,196,0.90)' }}>
                          The private platform lets residents post plans, invite others to join, and coordinate attendance for major Vegas events. It's not a public feed — it's a private community tool.
                        </p>
                        <p className="text-[15px] leading-relaxed mb-8" style={{ color: 'rgba(167,163,196,0.75)' }}>
                          Golden Knights game next Friday? Post it. Heading to the Sphere? See who else is going. Looking for dinner company? The community is there.
                        </p>
                        <Link href="/request-access" className="btn-primary group inline-flex">
                          Request Access to Join
                          <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-0.5" />
                        </Link>
                      </div>
                      <div className="space-y-3">
                        {[
                          { icon: '🏒', text: 'Golden Knights — anyone going Friday?', meta: 'Posted by a resident · 4 responses' },
                          { icon: '🌐', text: 'Sphere show Saturday — 4 spots in the Escalade', meta: 'Posted by a resident · 7 responses' },
                          { icon: '🍽️', text: 'Dinner at Carbone — looking for 2 more', meta: 'Posted by a resident · 2 responses' },
                        ].map((item, i) => (
                          <div
                            key={i}
                            className="glass-card-dark p-4 flex items-start gap-3"
                            style={{ borderRadius: '10px', filter: 'blur(0px)' }}
                          >
                            <span className="text-[20px] flex-shrink-0">{item.icon}</span>
                            <div>
                              <p className="text-[13.5px] font-medium" style={{ color: '#F2F4FF' }}>{item.text}</p>
                              <p className="text-[11px] mt-1" style={{ color: 'rgba(167,163,196,0.50)' }}>{item.meta}</p>
                            </div>
                            <div
                              className="ml-auto flex items-center gap-1 px-2 py-1 rounded flex-shrink-0"
                              style={{ background: 'rgba(122,92,255,0.10)', border: '1px solid rgba(122,92,255,0.20)' }}
                            >
                              <Lock size={10} style={{ color: '#7A5CFF' }} />
                              <span className="text-[10px]" style={{ color: '#7A5CFF' }}>Members only</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* CTA */}
            <section className="py-20" style={{ background: '#07060C' }}>
              <div className="neon-divider mb-0" />
              <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 pt-20 text-center">
                <h2 className="font-display font-bold text-[32px] sm:text-[40px] leading-tight tracking-tight mb-4" style={{ color: '#F2F4FF' }}>
                  Interested in living at Panorama?
                </h2>
                <p className="text-[16px] max-w-xl mx-auto mb-8" style={{ color: '#A7A3C4' }}>
                  Request access to the private resident community or explore what Panorama has to offer.
                </p>
                <div className="flex flex-wrap items-center justify-center gap-4">
                  <Link href="/request-access" className="btn-primary group">
                    Request Access
                    <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-0.5" />
                  </Link>
                  <Link href="/why-panorama" className="btn-secondary">
                    Why Panorama
                  </Link>
                </div>

                {/* Cross-links */}
                <div
                  className="mt-14 pt-10 border-t"
                  style={{ borderColor: 'rgba(255,255,255,0.06)' }}
                >
                  <p className="text-[11px] font-semibold tracking-[0.10em] uppercase mb-5" style={{ color: 'rgba(167,163,196,0.35)' }}>
                    Explore more
                  </p>
                  <div className="flex flex-wrap items-center justify-center gap-3">
                    <Link href="/amenities" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      Amenities
                    </Link>
                    <Link href="/lifestyle" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      The Lifestyle
                    </Link>
                    <Link href="/resources" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      Resources
                    </Link>
                    <Link href="/real-estate" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      Real Estate
                    </Link>
                    <Link href="/faq" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      FAQ
                    </Link>
                  </div>
                </div>
              </div>
            </section>
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
